import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { formatINR } from "@/lib/api";
import Seo from "@/components/Seo";
import { useStore } from "@/context/StoreContext";

export default function Cart() {
  const { cart, updateQty, removeFromCart, cartTotal } = useStore();
  const navigate = useNavigate();

  return (
    <div className="pt-28 pb-16 max-w-[1600px] mx-auto px-6 md:px-10 min-h-[70vh]">
      <Seo title="Your Cart — Indoforce" description="Review items in your cart." />
      <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none mb-12">Cart</h1>

      {cart.length === 0 ? (
        <div className="text-center py-20 border border-white/10" data-testid="empty-cart">
          <ShoppingBag size={40} className="mx-auto text-white/30 mb-6" />
          <p className="text-white/60 mb-6">Your cart is empty.</p>
          <Link to="/shop" className="btn-fill inline-flex items-center gap-3 border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest">
            Start shopping <ArrowRight size={18} />
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 border border-white/10 divide-y divide-white/10">
            {cart.map((item) => (
              <div key={item.product_id} className="flex gap-5 p-5" data-testid={`cart-item-${item.product_id}`}>
                <div className="w-24 h-24 bg-[#161616] border border-white/10 overflow-hidden shrink-0">
                  {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold uppercase tracking-tight leading-tight">{item.name}</h3>
                  <p className="text-[#E31E24] font-display font-black mt-1">{formatINR(item.price)}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-white/20">
                      <button onClick={() => updateQty(item.product_id, item.qty - 1)} className="p-2 hover:text-[#E31E24]" data-testid={`cart-minus-${item.product_id}`}><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm">{item.qty}</span>
                      <button onClick={() => updateQty(item.product_id, item.qty + 1)} className="p-2 hover:text-[#E31E24]" data-testid={`cart-plus-${item.product_id}`}><Plus size={14} /></button>
                    </div>
                    <button onClick={() => removeFromCart(item.product_id)} className="text-white/40 hover:text-[#E63946] p-2" data-testid={`cart-remove-${item.product_id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="font-display font-black hidden sm:block">{formatINR(item.price * item.qty)}</div>
              </div>
            ))}
          </div>

          <div className="border border-white/10 p-6 h-fit lg:sticky lg:top-28">
            <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-6">Summary</h3>
            <div className="flex justify-between text-white/60 mb-3"><span>Subtotal</span><span data-testid="cart-subtotal">{formatINR(cartTotal)}</span></div>
            <div className="flex justify-between text-white/60 mb-3"><span>Shipping</span><span>Calculated at checkout</span></div>
            <div className="flex justify-between font-display font-black text-2xl border-t border-white/10 pt-4 mt-4">
              <span>Total</span><span className="text-[#E31E24]">{formatINR(cartTotal)}</span>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              data-testid="checkout-btn"
              className="btn-fill w-full mt-6 flex items-center justify-center gap-3 border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest"
            >
              Checkout <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
